import { Accordion } from '@zoopla-eng/zds-components/accordion';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button, IconButton } from '@zoopla-eng/zds-components/button';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { SectionTitleSmall } from '@zoopla-eng/zds-components/headings';
import { CloseMedium } from '@zoopla-eng/zds-components/icons';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { Added, AddedToSite } from './AddedToSite';
import { Availability, AvailableFrom } from './Availability';
import { Bedrooms, BedsMax, BedsMin } from './Bedrooms';
import { Features } from './Features';
import { FurnishedState, Furnishing } from './Furnishing';
import { Keywords } from './Keywords';
import {
    BillsIncluded,
    IncludeRented,
    IncludeSharedAccommodation,
    LettingType,
    PetsAllowed,
    RentalTerm
} from './LettingType';
import { Price, PriceFrequency, PriceMax, PriceMin } from './Price';
import { PropertySubType, PropertyType } from './PropertyType';
import { trackFilterClick } from './utils/';
import {
    getDefaultToRentFilters,
    getToRentFilters
} from './utils/getToRentFilters';

const getSearchPath = (
    propertyType: PropertyType | 'retirement' | 'bungalow',
    searchPath: string | string[]
) => {
    const searchPaths = {
        property: 'property',
        houses: 'houses',
        flats: 'flats',
        farms_land: 'property',
        retirement: 'retirement',
        bungalow: 'bungalows'
    };

    if (!searchPath || typeof searchPath === 'string') {
        return [searchPaths[propertyType]];
    }

    return [searchPaths[propertyType], ...searchPath.slice(1)];
};

type ToRentFiltersProps = {
    onClose: () => void;
    onUpdate: () => void;
};

export const ToRentFilters = ({ onClose, onUpdate }: ToRentFiltersProps) => {
    const { query, push, pathname } = useRouter();

    const [isLoading, setIsLoading] = useState(false);

    const [accordionValue, updateAccordionValue] = useState<string[]>([]);

    const [formValue, updateFormValue] = useState(getToRentFilters(query));

    const onSubmit = async (e: { preventDefault: () => void }) => {
        setIsLoading(true);

        e.preventDefault();

        const updatedQuery = {
            ...query,
            ...formValue,
            'search-path': getSearchPath(
                formValue['property_type'] as PropertyType,
                query['search-path'] ?? ''
            )
        };

        if (
            formValue['property_sub_type'].length === 1 &&
            formValue['property_sub_type'][0] === 'bungalow'
        ) {
            updatedQuery['search-path'] = getSearchPath(
                'bungalow',
                updatedQuery['search-path']
            );
        }

        if (formValue['property_type'] !== 'farms_land') {
            delete (updatedQuery as Partial<typeof updatedQuery>)[
                'property_type'
            ];
        }

        Object.keys(updatedQuery).forEach((key) => {
            if (updatedQuery[key as keyof typeof updatedQuery] === '') {
                delete updatedQuery[key as keyof typeof updatedQuery];
            }
        });

        await push({
            pathname: pathname,
            query: {
                ...updatedQuery
            }
        });

        setIsLoading(false);

        onUpdate();
    };

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Stack
                alignItems="center"
                flexDirection="row"
                justifyContent="space-between"
                {...{
                    pl: '$S500',
                    pr: '$S200',
                    py: '$S200'
                }}
            >
                <Button
                    category="tertiary"
                    onClick={() => {
                        trackFilterClick();
                        updateFormValue(getDefaultToRentFilters());
                    }}
                >
                    Reset
                </Button>
                <SectionTitleSmall>Filter your results</SectionTitleSmall>
                <IconButton
                    category="tertiary"
                    label="close"
                    onClick={() => {
                        onClose();
                    }}
                >
                    <CloseMedium />
                </IconButton>
            </Stack>
            <Divider direction="horizontal" />
            <Stack
                flexDirection="column"
                flexWrap="nowrap"
                rowGap="$S500"
                flexGrow="1"
                {...{
                    overflowY: 'auto'
                }}
            >
                <Box width="100%" bpM100={{ px: 'calc($L400 - $S400)' }}>
                    <Accordion
                        value={accordionValue}
                        panels={[
                            {
                                label: 'Bedrooms',
                                value: 'bedrooms',
                                component: (
                                    <Bedrooms
                                        value={{
                                            bedsMin:
                                                formValue.beds_min as BedsMin,
                                            bedsMax:
                                                formValue.beds_max as BedsMax
                                        }}
                                        onChange={function ({
                                            bedsMin,
                                            bedsMax
                                        }: {
                                            bedsMin: string;
                                            bedsMax: string;
                                        }): void {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                beds_min: bedsMin,
                                                beds_max: bedsMax
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Price',
                                value: 'price',
                                component: (
                                    <Price
                                        value={{
                                            priceMin:
                                                formValue.price_min as PriceMin,
                                            priceMax:
                                                formValue.price_max as PriceMax,
                                            priceFrequency:
                                                (formValue.price_frequency ||
                                                    'per_month') as PriceFrequency
                                        }}
                                        onChange={({
                                            priceMin,
                                            priceMax,
                                            priceFrequency
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                price_min: priceMin,
                                                price_max: priceMax,
                                                price_frequency: priceFrequency
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Added to site',
                                value: 'added',
                                component: (
                                    <AddedToSite
                                        value={formValue.added as Added}
                                        onChange={(added: Added) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                added: added
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Availability',
                                value: 'available_from',
                                component: (
                                    <Availability
                                        value={
                                            formValue.available_from as AvailableFrom
                                        }
                                        onChange={(
                                            availableFrom: AvailableFrom
                                        ) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                available_from: availableFrom
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Property type',
                                value: 'property_type',
                                component: (
                                    <PropertyType
                                        value={{
                                            propertyType:
                                                formValue.property_type as PropertyType,
                                            propertySubType:
                                                formValue.property_sub_type as PropertySubType
                                        }}
                                        onChange={({
                                            propertyType,
                                            propertySubType
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                property_type: propertyType,
                                                property_sub_type:
                                                    propertySubType
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Letting type',
                                value: 'letting_type',
                                component: (
                                    <LettingType
                                        value={{
                                            rentalTerm:
                                                formValue.rental_term as RentalTerm,
                                            billsIncluded:
                                                formValue.bills_included as BillsIncluded,
                                            petsAllowed:
                                                formValue.pets_allowed as PetsAllowed,
                                            includeRented:
                                                formValue.include_rented as IncludeRented,
                                            includeSharedAccommodation:
                                                formValue.include_shared_accommodation as IncludeSharedAccommodation
                                        }}
                                        onChange={({
                                            rentalTerm,
                                            billsIncluded,
                                            petsAllowed,
                                            includeSharedAccommodation,
                                            includeRented
                                        }) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                rental_term: rentalTerm,
                                                bills_included: billsIncluded,
                                                pets_allowed: petsAllowed,
                                                include_shared_accommodation:
                                                    includeSharedAccommodation,
                                                include_rented: includeRented
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Furnishing',
                                value: 'furnishing',
                                component: (
                                    <Furnishing
                                        value={
                                            formValue.furnished_state as FurnishedState
                                        }
                                        onChange={(
                                            furnishedState: FurnishedState
                                        ) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                furnished_state: furnishedState
                                            }));
                                        }}
                                    />
                                )
                            },
                            {
                                label: 'Features',
                                value: 'features',
                                component: (
                                    <Features
                                        value={formValue.feature as Features}
                                        onChange={(feature) => {
                                            trackFilterClick();
                                            updateFormValue((oldFormValue) => ({
                                                ...oldFormValue,
                                                feature: feature
                                            }));
                                        }}
                                    />
                                )
                            }
                        ]}
                        onValueChange={updateAccordionValue}
                        maxWidth="100%"
                    />
                </Box>
                <Box px="$S400" pb="$L100" bpM100={{ px: '$L400' }}>
                    <Keywords
                        value={formValue.keywords as Keywords}
                        onChange={(keywords) => {
                            trackFilterClick();
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                keywords: keywords
                            }));
                        }}
                    />
                </Box>
            </Stack>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                px="$S400"
                py="$S400"
                {...{
                    boxShadow: '$200',
                    zIndex: '$dropdown'
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    width="100%"
                    bpM100={{ px: 'calc($L400 - $S400)' }}
                >
                    <Button
                        disabled={isLoading}
                        category="primary"
                        onClick={onSubmit}
                    >
                        Update Results
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};
